import { useState } from "react";
import { useAdminAuditLogs } from "@/hooks/use-admin-audit-logs";
import { AuditFilters, AuditLog } from "@/types/audit";
import { AuditFiltersComponent } from "../AuditFilters";
import { AuditDetailModal } from "../AuditDetailModal";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Download, Eye } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { getActionLabel, getEntityTypeLabel } from "@/lib/audit/action-labels";
import { getActionCategory, CATEGORY_COLORS } from "@/lib/audit/action-categories";
import { exportAuditToCSV, exportAuditToExcel } from "@/lib/audit/export-utils";
import { toast } from "sonner";

export function AdminTab() {
  const [filters, setFilters] = useState<AuditFilters>({});
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const { data: logs, isLoading } = useAdminAuditLogs(filters);

  const handleExportCSV = () => {
    if (!logs || logs.length === 0) {
      toast.error('Nenhum dado para exportar');
      return;
    }
    exportAuditToCSV(logs as any);
    toast.success('Relatório CSV exportado com sucesso');
  };

  const handleViewDetails = (log: any) => {
    setSelectedLog(log as AuditLog);
    setDetailModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <AuditFiltersComponent onFiltersChange={setFilters} />

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Logs Administrativos</CardTitle>
              {logs && logs.length > 0 && (
                <p className="text-sm text-muted-foreground mt-1">
                  Exibindo {logs.length} registro(s)
                </p>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={!logs || logs.length === 0}>
              <Download className="h-4 w-4 mr-2" />
              Exportar CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : !logs || logs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p>Nenhum registro encontrado</p>
            </div>
          ) : (
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/Hora</TableHead>
                    <TableHead>Usuário</TableHead>
                    <TableHead>Ação</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Empresa</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => {
                    const category = getActionCategory(log.action);
                    const colors = CATEGORY_COLORS[category];
                    
                    return (
                      <TableRow key={log.id}>
                        <TableCell className="font-mono text-xs">
                          {format(new Date(log.created_at), 'dd/MM/yy HH:mm', { locale: ptBR })}
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {log.actor_user_id?.substring(0, 8)}...
                        </TableCell>
                        <TableCell>
                          <Badge className={colors.badge} variant="outline">
                            {getActionLabel(log.action)}
                          </Badge>
                        </TableCell>
                        <TableCell>{getEntityTypeLabel(log.entity_type)}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {log.company_id ? `${log.company_id.substring(0, 8)}...` : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetails(log)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <AuditDetailModal
        log={selectedLog}
        open={detailModalOpen}
        onOpenChange={setDetailModalOpen}
      />
    </div>
  );
}
